import type { RuleEvaluationResult } from '@billing/validation/rules';
import type { UseFormReturnType } from '@mantine/form';
import { cloneDeep, get, isEqual, set } from 'lodash';
import { useEffect } from 'react';
import { isRecord } from 'utils/objectUtils';
import { useRemoteFeatureFlag } from './useRemoteFeatureFlag';

interface LiveRuleDataState<Values> {
  evaluation: RuleEvaluationResult | null;
  isChecking: boolean;
  isStale: boolean;
  setEvaluationBaseline: (formValues: Values, appliedFields: string[]) => void;
}

type LiveRuleDataForm<Values> = Pick<
  UseFormReturnType<Values>,
  'getValues' | 'setValues'
>;

const RULE_MANAGED_TARIFF_FIELDS = [
  'units',
  'anesthesiaUnits',
  'percentage',
  'incision',
] as const;

const getTariffIdentity = (tariff: Record<string, unknown>): string | null => {
  if (typeof tariff.id === 'string' && tariff.id) return `id:${tariff.id}`;
  if (typeof tariff._rowID === 'string' && tariff._rowID) {
    return `row:${tariff._rowID}`;
  }
  if (tariff.code != null && tariff.prefix != null) {
    return `code:${String(tariff.prefix)}:${String(tariff.code)}`;
  }
  return null;
};

const reconcileAppliedTariffs = (
  currentValue: unknown,
  appliedValue: unknown
): unknown => {
  if (!Array.isArray(currentValue) || !Array.isArray(appliedValue)) {
    return appliedValue;
  }

  const currentByIdentity = new Map<string, Array<Record<string, unknown>>>();
  currentValue.forEach((tariff) => {
    if (!isRecord(tariff)) return;
    const identity = getTariffIdentity(tariff);
    if (!identity) return;

    const matchingTariffs = currentByIdentity.get(identity) ?? [];
    matchingTariffs.push(tariff);
    currentByIdentity.set(identity, matchingTariffs);
  });

  return appliedValue.map((appliedTariff) => {
    if (!isRecord(appliedTariff)) return appliedTariff;
    const identity = getTariffIdentity(appliedTariff);
    const currentTariff = identity
      ? currentByIdentity.get(identity)?.shift()
      : undefined;
    if (!currentTariff) return appliedTariff;

    const reconciledTariff = { ...appliedTariff, ...currentTariff };
    RULE_MANAGED_TARIFF_FIELDS.forEach((field) => {
      if (Object.hasOwn(appliedTariff, field)) {
        reconciledTariff[field] = appliedTariff[field];
      }
    });
    return reconciledTariff;
  });
};

const resolveAppliedValue = (
  field: string,
  currentValue: unknown,
  appliedValue: unknown
): unknown =>
  field === 'tariffs' || field.endsWith('.tariffs')
    ? reconcileAppliedTariffs(currentValue, appliedValue)
    : appliedValue;

const hasBlockingViolation = (evaluation: RuleEvaluationResult): boolean =>
  evaluation.violations.some((violation) => violation.severity === 'block');

export const useApplyLiveRuleData = <Values extends object>(
  form: LiveRuleDataForm<Values>,
  liveRules?: LiveRuleDataState<Values>
): void => {
  const isApplyDataEnabled = useRemoteFeatureFlag('live_rules_apply_data');

  useEffect(() => {
    const evaluation = liveRules?.evaluation;
    if (
      !isApplyDataEnabled ||
      !evaluation ||
      liveRules.isChecking ||
      liveRules.isStale ||
      hasBlockingViolation(evaluation)
    ) {
      return;
    }

    const currentValues = form.getValues();
    const changedEntries = Object.entries(evaluation.appliedData)
      .map(([field, appliedValue]) => {
        const currentValue = get(currentValues, field);
        return [
          field,
          resolveAppliedValue(field, currentValue, appliedValue),
        ] as const;
      })
      .filter(([field, value]) => !isEqual(get(currentValues, field), value));

    if (changedEntries.length === 0) return;

    const nextValues = cloneDeep(currentValues);
    changedEntries.forEach(([field, value]) => {
      set(nextValues, field, value);
    });
    const tariffFieldsToReconcile = changedEntries
      .map(([field]) => field)
      .filter((field) => field === 'tariffs' || field.endsWith('.tariffs'));
    liveRules.setEvaluationBaseline(nextValues, tariffFieldsToReconcile);
    form.setValues(nextValues);
  }, [
    form,
    isApplyDataEnabled,
    liveRules?.evaluation,
    liveRules?.isChecking,
    liveRules?.isStale,
  ]);
};
